/**
 * What the dashboard does when a Keycloak token refresh fails.
 *
 * Never `logout()`: that ends the SSO session, which is how a Keycloak blip
 * used to sign every open tab out. While the access token in hand is valid it
 * keeps working (the gateway verifies it against cached JWKS, not Keycloak),
 * so every call goes out with it and the AuthProvider timer retries.
 *
 * Nor `login()` merely because that token has expired. The timer always tries
 * the refresh first, so a failure here means Keycloak just failed to answer: a
 * login() redirect now would land on the same outage and throw the page away.
 * The refresh token outlives the access token by far (SSO idle 24 h), so the
 * first tick after Keycloak returns refreshes silently with no navigation.
 * Until then calls fail with AuthRefreshFailed instead of going out.
 * `login()` is for when no token is held: keycloak-js cleared it, or init
 * never produced one.
 *
 * The decision reads the token, not the rejection. keycloak-js 26 rejects
 * `updateToken` with a `TypeError` when no response was readable and with a
 * `NetworkError` (absent from its type declarations) carrying the Response for
 * any other non-2xx; where Keycloak is on another origin (prod's api.fastn.dev)
 * a load balancer 5xx has no CORS headers and is a `TypeError` as well. Only a
 * 400 (`invalid_grant`) says the session is gone, and keycloak-js acts on any
 * 400 before the promise rejects: it clears the tokens and, under
 * `onLoad: "login-required"`, calls `login()` itself. A cleared token reads as
 * `null` below, so the token alone tells apart every case the error could.
 *
 * Retries stay near the timer's pace: the fetch path adds at most one per
 * 30 s, plus one for a call the gateway has just refused. The realm rotates
 * refresh tokens (`refreshTokenMaxReuse: 0`), so a refresh that failed after
 * Keycloak had rotated it leaves a spent token in hand (see org-claim.ts), and
 * a tight retry would replay it. The next attempt reaches the same 400 and the
 * same keycloak-js login.
 */

import type Keycloak from "keycloak-js";

/** How often AuthProvider refreshes, and the floor between fetch-path retries after a failure. */
export const REFRESH_INTERVAL_MS = 30_000;

/**
 * AuthProvider refreshes once the token has less than this left. Against a
 * 300 s token and a 30 s tick that happens with 90 to 120 s remaining, so the
 * token in hand never has less than 90 s when Keycloak goes away.
 */
export const REFRESH_MIN_VALIDITY_S = 120;

export type AuthAction =
  /** The token in hand is valid: keep sending it; the next tick retries. */
  | { kind: "wait"; secondsUntilExpiry: number }
  /** Expired but never refused: send nothing; the next tick retries. */
  | { kind: "expired" }
  /** No token is held: sign in again (silently while the SSO cookie lives). */
  | { kind: "login" };

export function nextAuthAction(secondsUntilExpiry: number | null): AuthAction {
  if (secondsUntilExpiry === null) return { kind: "login" };
  if (secondsUntilExpiry > 0) return { kind: "wait", secondsUntilExpiry };
  return { kind: "expired" };
}

/**
 * Seconds before the access token in hand expires, corrected to the server's
 * clock the way keycloak-js `isTokenExpired` does it. `null` when there is no
 * token, including after keycloak-js cleared it on a rejected refresh.
 */
export function secondsUntilExpiry(
  kc: Pick<Keycloak, "tokenParsed" | "timeSkew">,
  nowMs: number = Date.now(),
): number | null {
  const exp = kc.tokenParsed?.exp;
  if (typeof exp !== "number") return null;
  return exp - Math.ceil(nowMs / 1000) + (kc.timeSkew ?? 0);
}
